import api from "../../libs/axios";
import toast from "react-hot-toast";
import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../../providers/auth-provider";
import { Link } from "react-router-dom";
import { UAParser } from "ua-parser-js";
import validator from "validator";

export default function Login(): React.ReactElement {
    const [loading, setLoading] = useState(false);
    const [deviceId, setDeviceId] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [otpMethod, setOtpMethod] = useState<"whatsapp" | "sms">("whatsapp");
    const [otpLoading, setOtpLoading] = useState(false);
    const [showOTPSection, setShowOTPSection] = useState(false);
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [resendTimer, setResendTimer] = useState(0);
    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
    const { login } = useAuth();

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    const getDeviceInfo = () => {
        const parser = new UAParser();
        const result = parser.getResult();

        // Get device name (brand + model or OS name)
        let deviceName = "";
        if (result.device.vendor && result.device.model) {
            deviceName = `${result.device.vendor} ${result.device.model}`;
        } else if (result.os.name) {
            deviceName = result.os.name;
        } else {
            deviceName = "Unknown Device";
        }

        // Get device type
        let deviceType = "desktop"; // default
        if (result.device.type) {
            deviceType = result.device.type;
        } else if (result.os.name === "iOS" || result.os.name === "Android") {
            deviceType = "mobile";
        }

        return {
            name: deviceName,
            type: deviceType
        };
    };

    const validatePhoneNumber = (phone: string) => {
        if (validator.isEmpty(phone)) {
            setPhoneError("Phone number is required");
            return false;
        }
        if (!validator.isMobilePhone(phone, "en-IN")) {
            setPhoneError("Please enter a valid phone number");
            return false;
        }
        setPhoneError("");
        return true;
    };

    const handleOtpChange = (index: number, value: string) => {
        if (value.length > 1) {
            // Handle paste
            const pastedData = value.slice(0, 6);
            const newOtp = [...otp];
            for (let i = 0; i < pastedData.length && i + index < 6; i++) {
                newOtp[index + i] = pastedData[i];
            }
            setOtp(newOtp);

            // Focus on the last filled input or next empty one
            const nextIndex = Math.min(index + pastedData.length, 5);
            otpRefs.current[nextIndex]?.focus();
        } else {
            // Single character input
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            // Auto-focus next input
            if (value && index < 5) {
                otpRefs.current[index + 1]?.focus();
            }
        }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const onSendOTP = async () => {
        // Validation
        const isPhoneValid = validatePhoneNumber(phoneNumber);

        if (!isPhoneValid) {
            return;
        }

        setLoading(true);
        try {
            const deviceInfo = getDeviceInfo();

            const response = await api.post("/auth/send-otp", {
                phone_number: phoneNumber,
                device_info: deviceInfo,
                otp_method: otpMethod
            });
            setDeviceId(response.data.device_id);
            toast.success("OTP sent successfully!");
            setShowOTPSection(true);
            setResendTimer(30);
        } catch (error: any) {
            console.error("OTP Send Error Response:", error?.response?.data || error);
            toast.error(error?.response?.data?.message || "Failed to send OTP");
        } finally {
            setLoading(false);
        }
    };

    const onVerifyOTP = async () => {
        const otpCode = otp.join("");
        if (otpCode.length !== 6) {
            toast.error("Please enter complete OTP");
            return;
        }

        setOtpLoading(true);
        try {
            const response = await api.post("/auth/verify-otp", {
                phone_number: phoneNumber,
                otp_code: otpCode,
                device_id: deviceId
            });
            if (!response.data.profile_registered) {
                toast.error("This account is not registered. You not be able to login.");
                return;
            }
            toast.success("Login successful!");
            login(response.data.access_token);
        } catch (error: any) {
            console.error("OTP Verify Error Response:", error?.response?.data || error);
            toast.error(error?.response?.data?.message || "Invalid OTP");
        } finally {
            setOtpLoading(false);
        }
    };

    const onChangePhoneNumber = () => {
        setShowOTPSection(false);
        setOtp(["", "", "", "", "", ""]);
        setPhoneError("");
        setResendTimer(0);
    };

    const isOtpComplete = otp.every(digit => digit !== "");

    return (
        <div className="">
            <div className="min-h-[calc(100vh-70px)] flex flex-col items-center justify-center py-6 px-4">
                <div className="max-w-[480px] w-full">
                    <div className="p-4 sm:px-8 py-12 rounded-2xl bg-white border border-gray-200 shadow-sm">
                        <div className="text-center flex flex-col items-center space-y-2">
                            <img
                                src="/assets/images/logo.png"
                                alt="CrikStars"
                                className="h-28 mb-5 w-auto"
                            />
                            <h1 className="text-slate-900 text-center text-3xl font-semibold">
                                Welcome, Admin!
                            </h1>
                            <p className="text-slate-600">
                                {showOTPSection ? "" : "Login to your account"}
                            </p>
                        </div>

                        <form className="mt-6 space-y-5" onSubmit={(e) => e.preventDefault()}>
                            {/* Phone Number Section */}
                            <div>

                                {showOTPSection ? (
                                    <div className="text-center space-y-4">
                                        <div className="text-center">
                                            <p className="text-slate-600 text-center text-sm">
                                                Verification code sent to: <span className="text-slate-900 font-semibold"> +91 {phoneNumber}</span>
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={onChangePhoneNumber}
                                            className="inline-flex items-center text-sm text-blue-500 hover:text-blue-600 font-medium"
                                        >
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 17l-5-5m0 0l5-5m-5 5h12"></path>
                                            </svg>
                                            Change phone number
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <label className="text-slate-900 text-sm font-medium mb-2 block">
                                            Phone Number
                                        </label>

                                        <div className="relative flex items-center">
                                            <span className="absolute border-r pr-1.5 left-2 top-1/2 -translate-y-1/2 text-gray-500">
                                                +91
                                            </span>
                                            <input
                                                name="phoneNumber"
                                                type="tel"
                                                value={phoneNumber}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D+/g, '');
                                                    setPhoneNumber(value);
                                                    if (phoneError) {
                                                        validatePhoneNumber(value);
                                                    }
                                                }}
                                                onBlur={() => validatePhoneNumber(phoneNumber)}
                                                className={`w-full pl-12 pr-10 text-slate-900 border px-4 py-2.5 rounded-md outline-primary-600 ${phoneError
                                                    ? 'border-red-500 focus:border-red-500'
                                                    : 'border-slate-300'
                                                    }`}
                                                placeholder="Enter phone number"
                                                maxLength={10}
                                                pattern="[0-9]*"
                                                inputMode="numeric"
                                            />
                                        </div>
                                        <div className="mt-2 flex items-center gap-3 text-sm">
                                            <span className="text-slate-500">Send code via:</span>
                                            <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name="otpMethod" checked={otpMethod === "whatsapp"} onChange={() => setOtpMethod("whatsapp")} /> WhatsApp</label>
                                            <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name="otpMethod" checked={otpMethod === "sms"} onChange={() => setOtpMethod("sms")} /> SMS</label>
                                        </div>
                                        {phoneError && (
                                            <p className="mt-1 text-sm text-red-600">{phoneError}</p>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* OTP Section */}
                            {showOTPSection && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-slate-900 text-sm font-medium mb-4 block text-center">
                                            Enter 6-digit verification code
                                        </label>
                                        <div className="flex gap-2 md:gap-3 justify-center mb-6">
                                            {otp.map((digit, index) => (
                                                <input
                                                    key={index}
                                                    ref={(el) => {
                                                        otpRefs.current[index] = el;
                                                    }} type="text"
                                                    value={digit}
                                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                    className="h-10 w-10 md:w-14 md:h-14 text-center text-xl font-bold text-slate-900 border-2 border-slate-300 rounded-xl outline-none focus:border-primary-600 focus:ring-2 focus:ring-primary-100 transition-all duration-200 hover:border-slate-400"
                                                    maxLength={6}
                                                    inputMode="numeric"
                                                />
                                            ))}
                                        </div>
                                        <div className="flex items-center justify-center text-center gap-4">
                                            <p className="text-sm text-slate-500">
                                                Didn't receive the code?
                                            </p>
                                            <button
                                                type="button"
                                                onClick={onSendOTP}
                                                disabled={loading || resendTimer > 0}
                                                className="inline-flex items-center text-sm text-blue-500 hover:text-blue-600 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                                                </svg>
                                                {loading ? "Sending..." : resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend Code"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Action Button */}
                            <div className="!mt-7">
                                {showOTPSection ? (
                                    <button
                                        type="button"
                                        disabled={otpLoading || !isOtpComplete}
                                        onClick={onVerifyOTP}
                                        className={`w-full py-3 px-4 text-base font-semibold tracking-wide rounded-xl text-white transition-all duration-200 ${otpLoading || !isOtpComplete
                                            ? "bg-primary-400 cursor-not-allowed"
                                            : "bg-primary-600 hover:bg-primary-700 hover:shadow-lg transform hover:-translate-y-0.5"
                                            }`}
                                    >
                                        {otpLoading ? "Verifying..." : "Verify & Continue"}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        disabled={loading}
                                        onClick={onSendOTP}
                                        className={`w-full py-3 px-4 text-base font-semibold tracking-wide rounded-xl text-white transition-all duration-200 ${loading ? "bg-primary-400 cursor-not-allowed" : "bg-primary-600 hover:bg-primary-700 hover:shadow-lg transform hover:-translate-y-0.5"
                                            }`}
                                    >
                                        {loading ? "Sending OTP..." : "Send OTP"}
                                    </button>
                                )}
                            </div>

                            <div className="text-center mt-6 text-sm text-slate-600">
                                <p>
                                    By continuing, you agree to our{" "}
                                    <Link to="/terms-and-conditions" className="text-primary-600 hover:underline">
                                        Terms & Conditions
                                    </Link>{" "} <br />
                                    and{" "}
                                    <Link to="/privacy-policy" className="text-primary-600 hover:underline">
                                        Privacy Policy
                                    </Link>.
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}